import type { ReactNode } from 'react'

import Stack from '@mui/material/Stack'
import type { SxProps } from '@mui/material/styles'

import { Text } from '@mxenabled/mxui'

import AmountPill from './AmountPill'
import LogoTile from './LogoTile'

interface MerchantHighlightCardProps {
  /** Brand/merchant logo visual (rendered inside a `LogoTile`). */
  logo: ReactNode
  /** Merchant/payee name. */
  name: ReactNode
  /** Optional label above the name (e.g. "{Date}"). */
  eyebrow?: ReactNode
  /** Amount. Rendered as a dark pill when `amountVariant='pill'`, else plain. */
  amount?: ReactNode
  /** How to render the amount. */
  amountVariant?: 'pill' | 'text'
  /** Typography variant for the name. Defaults to `h2` for `pill`, else `h3`. */
  nameVariant?: 'h2' | 'h3'
  /** Trailing element beneath the name (e.g. a `<StatusPill>`); replaces `amount`. */
  trailing?: ReactNode
  /** Card surface color token. */
  bgcolor?: string
  /** CSS background (e.g. a gradient) for the card surface; overrides `bgcolor`. */
  background?: string
  /** Card border color token (e.g. New Merchant's tinted card). */
  borderColor?: string
  /** Render the logo's drop-shadow. Default true; false for MSA V2's Visual. */
  logoShadow?: boolean
  sx?: SxProps
}

/**
 * A merchant/brand highlight card: a `LogoTile` beside a text column (optional
 * eyebrow, name, amount/trailing). Shared by Peer-to-Peer Categorization, New
 * Merchant, and Monthly Subscription Aggregate V2's Visual state.
 */
const MerchantHighlightCard = ({
  amount,
  amountVariant = 'text',
  background,
  bgcolor = 'grey.200',
  borderColor,
  eyebrow,
  logo,
  logoShadow = true,
  name,
  nameVariant,
  trailing,
  sx,
}: MerchantHighlightCardProps) => {
  const resolvedNameVariant = nameVariant ?? (amountVariant === 'pill' ? 'h2' : 'h3')

  // `trailing` (e.g. a StatusPill) takes the amount's slot when provided;
  // otherwise render the amount as a dark pill or plain text.
  let amountNode: ReactNode = trailing
  if (amountNode == null && amount != null) {
    amountNode =
      amountVariant === 'pill' ? (
        <AmountPill>{amount}</AmountPill>
      ) : (
        <Text color="text.secondary" variant="body1">
          {amount}
        </Text>
      )
  }

  return (
    <Stack sx={{ px: 16, width: '100%', ...sx }}>
      <Stack
        sx={{
          alignItems: 'center',
          ...(background ? { background } : { bgcolor }),
          ...(borderColor && { border: '1px solid', borderColor }),
          borderRadius: '8px',
          flexDirection: 'row',
          gap: 16,
          p: 16,
          width: '100%',
        }}
      >
        <LogoTile shadow={logoShadow}>{logo}</LogoTile>
        <Stack sx={{ gap: 2, minWidth: 0 }}>
          {eyebrow && (
            <Text color="text.secondary" truncate={true} variant="caption">
              {eyebrow}
            </Text>
          )}
          <Text bold={true} truncate={true} variant={resolvedNameVariant}>
            {name}
          </Text>
          {amountNode}
        </Stack>
      </Stack>
    </Stack>
  )
}

export default MerchantHighlightCard
